import { technicianLine } from '@/scheduling/lib/calendar-mapping';
import type { CalendarDevice } from '@/types/scheduling-api';

/**
 * The card model the shared `PickerGrid` renders, and the pure functions that
 * build one (PROD-2193).
 *
 * These live in `lib/` rather than beside the component because a file that
 * exports both a component and a helper breaks Fast Refresh
 * (`react-refresh/only-export-components`) — and because the mapping from a
 * domain object to a card is exactly the sort of rule that is worth testing
 * without rendering anything.
 */

/**
 * One card. Three lines, in the visual weight the provider card established:
 *   title    — the thing's name.
 *   subtitle — what KIND of thing it is (a provider's speciality). Devices
 *              leave it empty; see `deviceCardItem`.
 *   meta     — the operational line, in brand colour (working hours /
 *              technician).
 * `subtitle` and `meta` are omitted entirely when blank.
 */
export interface PickerCardItem {
  id: string;
  title: string;
  subtitle?: string | null;
  meta?: string | null;
  photo?: string | null;
  initials?: string | null;
}

/** Two letters for the avatar fallback; the `Dr.` honorific never counts. */
export function pickerInitials(name: string): string {
  const parts = name.replace(/^Dr\.?\s*/i, '').split(' ').filter(Boolean);
  return parts.slice(0, 2).map((p) => p[0]).join('').toUpperCase() || '?';
}

/**
 * A device as a picker card: name, then the technician line.
 *
 * The subtitle slot is deliberately left EMPTY. It used to carry `subtype`,
 * and dev's subtypes are catalogue taxonomy — "Tier A - Mobile", "Tier C
 * diagnostic - Mobile" — which Andrew, 2026-09-14, does not want on any
 * screen: "tier info please remove it from all the screens if still exists".
 * The same vocabulary was already taken off the filter chips (#1010) and the
 * day-grid column headers (#1012); this is the card.
 *
 * Nothing replaces it. The slot means "what KIND of thing is this" (see
 * `PickerCardItem` above), and the only other things a device carries — room
 * and technician — are operational, which is what `meta` is for and where the
 * technician already is. Putting the room there would say the wrong thing in
 * the right-looking place. An omitted subtitle costs nothing visually:
 * `PickerGrid` renders the line only when it is truthy, so the card closes up
 * to name + technician rather than leaving a blank row.
 *
 * The technician line is `technicianLine`, the SAME rule the Devices strip
 * uses (PROD-2184), so an unassigned machine reads "No technician assigned"
 * in both places instead of two different ways.
 */
export function deviceCardItem(device: CalendarDevice): PickerCardItem {
  return {
    id: device.id,
    title: device.name,
    meta: technicianLine(device.technicianName),
    initials: pickerInitials(device.name),
  };
}
